import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, Zap, Phone, CheckCircle, AlertTriangle, Clock, Battery } from 'lucide-react';
import useSEO from '../../hooks/useSEO';

const QuehacerDespuesArrancar = () => {
  const navigate = useNavigate();

  // SEO: Set canonical to avoid duplicate content issues
  useSEO({
    title: 'Qué Hacer Después de Arrancar con la Batería Descargada | ArrancaYa BCN',
    description: 'Pasos esenciales después de arrancar tu coche con batería descargada. Evita quedarte tirado otra vez con estos consejos de ArrancaYa BCN Barcelona.',
    canonical: 'https://arrancaya-bcn.es/blog/que-hacer-despues-arrancar'
  });

  useEffect(() => {

    const metaKeywords = document.querySelector('meta[name="keywords"]') || document.createElement('meta');
    metaKeywords.name = 'keywords';
    metaKeywords.content = 'después de arrancar batería, qué hacer arranque coche, batería descargada Barcelona, recargar batería coche';
    document.head.appendChild(metaKeywords);

    // Schema markup
    const schema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "Qué Hacer Después de Arrancar con la Batería Descargada",
      "description": "Pasos esenciales para evitar problemas después de arrancar un coche con batería descargada",
      "image": "https://images.unsplash.com/photo-1558618047-3c8c6d6c3f3a?w=600&h=400&fit=crop",
      "totalTime": "PT30M",
      "step": [
        {
          "@type": "HowToStep",
          "name": "No apagar inmediatamente",
          "text": "Mantén el motor encendido mínimo 20-30 minutos para recargar la batería"
        },
        {
          "@type": "HowToStep", 
          "name": "Conducir sin parar",
          "text": "Conduce sin paradas para que el alternador recargue la batería"
        },
        {
          "@type": "HowToStep",
          "name": "Evitar consumos eléctricos",
          "text": "Apaga aire acondicionado, radio y luces innecesarias"
        },
        {
          "@type": "HowToStep",
          "name": "Comprobar voltaje",
          "text": "Verifica que la batería mantiene carga después del trayecto"
        }
      ],
      "author": {
        "@type": "Organization",
        "name": "ArrancaYa BCN"
      },
      "datePublished": "2026-01-13",
      "dateModified": "2026-01-13"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    // GTM tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'page_view',
      'page_title': 'Después de arrancar batería descargada',
      'page_location': window.location.href,
      'content_group1': 'Blog',
      'content_group2': 'Mantenimiento'
    });

    return () => {
      document.head.removeChild(script);
      if (metaKeywords.parentNode) {
        document.head.removeChild(metaKeywords);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gray-50 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-800">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al Blog
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-12">
          <div className="flex items-center space-x-4 text-sm text-gray-500 mb-6">
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">Mantenimiento</span>
            <span>•</span>
            <span>13 de enero, 2026</span>
            <span>•</span>
            <span>3 min de lectura</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-8 leading-tight">
            Qué Hacer <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Después</span> de Arrancar
          </h1>
          
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 border-2 border-green-100 mb-8">
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              Has conseguido arrancar tu coche, ¡genial! Pero ahora es crucial seguir estos pasos 
              para <strong className="text-red-600">evitar quedarte tirado otra vez</strong>. Consejos de ArrancaYa BCN.
            </p>
          </div>
          
          {/* Estadística impactante en lugar de imagen */}
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 shadow-lg">
            <div className="text-center">
              <div className="text-6xl font-black text-red-500 mb-4">85%</div>
              <p className="text-xl text-gray-700 font-medium">
                de los coches que se quedan sin batería <strong>vuelven a fallar</strong> en las próximas 48 horas si no sigues estos pasos
              </p>
            </div>
          </div>
        </header>

        <div className="prose prose-xl max-w-none space-y-12">
          
          {/* Alerta principal - MEJORADA */}
          <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-2xl p-10 text-white shadow-xl">
            <div className="flex items-start">
              <div className="bg-white/20 rounded-full p-4 mr-8 flex-shrink-0">
                <AlertTriangle className="h-10 w-10 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-6 text-white">⚠️ Atención: Tu Batería Sigue Débil</h2>
                <p className="text-red-100 text-xl leading-relaxed mb-0">
                  Aunque hayas arrancado, la batería puede estar <strong className="text-white">muy descargada</strong>. 
                  Si apagas el motor ahora, probablemente no vuelva a arrancar.
                </p>
              </div>
            </div>
          </div>

          <h2>Paso 1: NO Apagues el Motor (Mínimo 20-30 Minutos)</h2>
          
          <div className="bg-green-50 border-l-4 border-green-400 p-6 my-6">
            <div className="flex items-start">
              <Clock className="h-5 w-5 text-green-400 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-green-800 font-semibold mb-2">✅ Lo Más Importante</p>
                <p className="text-green-700 mb-0">
                  Mantén el motor funcionando <strong>mínimo 20-30 minutos</strong>. 
                  El alternador necesita este tiempo para enviar carga suficiente a la batería.
                </p>
              </div>
            </div>
          </div>

          <p><strong>¿Por qué es tan importante?</strong></p>
          <ul>
            <li>Una batería muy descargada necesita tiempo para recuperarse</li>
            <li>El alternador solo carga cuando el motor está funcionando</li>
            <li>Si apagas muy pronto, la batería no tendrá energía para el siguiente arranque</li>
            <li>Es mejor prevenir que volver a quedarse tirado</li>
          </ul>

          <h2>Paso 2: Conduce Sin Parar</h2>
          
          <p>La mejor forma de recargar la batería es <strong>conduciendo</strong>:</p>
          <ul>
            <li><strong>Conduce mínimo 30 minutos seguidos</strong></li>
            <li>Evita trayectos con muchas paradas (semáforos, atascos)</li>
            <li>La velocidad ideal es entre 40-80 km/h</li>
            <li>Si puedes, ve por autopista o vía rápida</li>
          </ul>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-6 my-6">
            <p className="text-blue-800 mb-0">
              <strong>💡 Consejo ArrancaYa:</strong> Si estás en Barcelona centro, 
              sal hacia las rondas o la autopista. El tráfico urbano con paradas constantes 
              no permite una recarga eficiente.
            </p>
          </div>

          <h2>Paso 3: Minimiza el Consumo Eléctrico</h2>
          
          <p>Mientras conduces, <strong>apaga todo lo que no sea esencial</strong>:</p>
          
          <div className="grid md:grid-cols-2 gap-6 my-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-red-800 font-bold mb-3">❌ Apagar Inmediatamente:</h3>
              <ul className="text-red-700 space-y-1">
                <li>• Aire acondicionado</li>
                <li>• Calefacción (solo si no es imprescindible)</li>
                <li>• Radio y sistema multimedia</li>
                <li>• Cargadores de móvil</li>
                <li>• Luces interiores</li>
                <li>• Luneta térmica</li>
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="text-green-800 font-bold mb-3">✅ Mantener Solo:</h3>
              <ul className="text-green-700 space-y-1">
                <li>• Luces de carretera (obligatorias)</li>
                <li>• Limpiaparabrisas (si llueve)</li>
                <li>• Dirección asistida</li>
                <li>• Instrumentación básica</li>
              </ul>
            </div>
          </div>

          <h2>Paso 4: Comprobar la Carga Después</h2>
          
          <p>Tras 30 minutos conduciendo, haz esta simple prueba:</p>
          <ol>
            <li><strong>Para el motor</strong> en un lugar seguro</li>
            <li><strong>Espera 5 minutos</strong> (para que se estabilice la batería)</li>
            <li><strong>Intenta arrancar de nuevo</strong></li>
          </ol>

          <div className="grid md:grid-cols-2 gap-6 my-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="flex items-center text-green-800 font-bold mb-3">
                <CheckCircle className="h-5 w-5 mr-2" />
                ✅ Si Arranca Bien:
              </h3>
              <p className="text-green-700 mb-0">
                Perfecto. Tu batería ha recuperado carga suficiente. 
                Aún así, vigila los próximos días por si se vuelve a descargar.
              </p>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="flex items-center text-red-800 font-bold mb-3">
                <AlertTriangle className="h-5 w-5 mr-2" />
                ❌ Si Cuesta Arrancar:
              </h3>
              <p className="text-red-700 mb-0">
                La batería puede estar agotada o tener problemas. 
                Es momento de cambiarla o revisar el alternador.
              </p>
            </div>
          </div>

          <h2>Paso 5: Señales de Alarma</h2>
          
          <p><strong>Llama a ArrancaYa BCN si observas:</strong></p>
          <ul>
            <li>El motor <strong>tiembla o va irregular</strong> tras arrancar</li>
            <li>Las luces del salpicadero <strong>parpadean</strong></li>
            <li>El motor se <strong>cala frecuentemente</strong></li>
            <li>Escuchas <strong>ruidos extraños</strong> del alternador</li>
            <li>La batería se vuelve a descargar en <strong>menos de 24 horas</strong></li>
          </ul>

          <h2>Prevención Para los Próximos Días</h2>

          <h3>Primeras 48 Horas:</h3>
          <ul>
            <li>Usa el coche <strong>a diario</strong> mínimo 15-20 minutos</li>
            <li>Evita trayectos muy cortos (menos de 5 minutos)</li>
            <li>No dejes luces o radio encendidos al aparcar</li>
          </ul>

          <h3>Si Vuelve a Pasar:</h3>
          <ul>
            <li>Probablemente necesites <strong>cambiar la batería</strong></li>
            <li>Puede haber un problema en el <strong>alternador</strong></li>
            <li>Existe alguna <strong>fuga eléctrica</strong> (consumo parásito)</li>
          </ul>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 my-8">
            <div className="flex items-start">
              <Battery className="h-5 w-5 text-yellow-400 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-yellow-800 font-semibold mb-2">🔋 Vida Útil de la Batería</p>
                <p className="text-yellow-700 mb-0">
                  Una batería de coche dura entre 3-5 años. Si la tuya tiene más de 3 años 
                  y se descarga frecuentemente, es hora de cambiarla.
                </p>
              </div>
            </div>
          </div>

          <h2>¿Cuándo Necesitas Ayuda Profesional?</h2>
          
          <p>Llama a ArrancaYa BCN si:</p>
          <ul>
            <li>El problema se repite en <strong>menos de una semana</strong></li>
            <li>Tu batería tiene <strong>más de 3 años</strong></li>
            <li>Notas síntomas de fallo del <strong>alternador</strong></li>
            <li>El coche hace ruidos extraños al arrancar</li>
            <li>No estás seguro de qué puede estar pasando</li>
          </ul>

          <p>Nuestros técnicos pueden:</p>
          <ul>
            <li>✅ <strong>Diagnosticar</strong> si es problema de batería o alternador</li>
            <li>✅ <strong>Cambiar la batería</strong> en el sitio</li>
            <li>✅ <strong>Comprobar</strong> el sistema eléctrico completo</li>
            <li>✅ <strong>Detectar fugas eléctricas</strong> (consumo parásito)</li>
          </ul>

        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-700 text-white rounded-lg p-8 mt-12">
          <div className="text-center">
            <Battery className="h-12 w-12 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">¿Tu Batería Sigue Dando Problemas?</h2>
            <p className="text-lg mb-6 opacity-90">
              En ArrancaYa BCN diagnosticamos y solucionamos problemas de batería y alternador. 
              <strong>Servicio completo en Barcelona</strong> con cambio de batería incluido.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
              >
                <Battery className="h-5 w-5 mr-2" />
                Diagnóstico + Arranque
              </Button>
              <Button
                onClick={() => window.open('tel:+34613076099')}
                variant="outline"
                className="bg-white text-orange-600 border-2 border-white hover:bg-orange-50 px-8 py-3"
              >
                <Phone className="h-5 w-5 mr-2" />
                Llamar: 613 076 099
              </Button>
            </div>
            <p className="text-sm mt-4 opacity-80">
              🔧 Diagnóstico profesional • 🔋 Cambio de batería in situ • ⚡ Reparación alternador
            </p>
          </div>
        </div>
      </article>
    </div>
  );
};

export default QuehacerDespuesArrancar;